import { ReactNode, useMemo } from 'react';

import { OnChangeFn, SortingState, VisibilityState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/ui/table/data-table';
import { CampaignStatus, CampaignSubscriber } from '@/shared/types/campaigns';

import { columns } from './columns';

interface CampaignLogsTableProps {
  data: CampaignSubscriber[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  currentPageIndex?: number;
  setCurrentPageIndex?: (index: number) => void;
  rowCount?: number;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  lastSeen: string[];
  onReplay: (sessionId: string) => void;
  campaign_senders?: Array<{ sender_id: string; integration_type?: string }>;
  onRowClick?: (row: CampaignSubscriber) => void;
  getRowClickable?: (row: CampaignSubscriber) => boolean;
  getRowHref?: (row: CampaignSubscriber) => string | null;
  onStopClick?: (subscriberId: string) => void;
  onResumeClick?: (subscriberId: string) => void;
  campaignStatus?: CampaignStatus;
  isAgentic?: boolean;
  hideReplay?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  paginationCenterContent?: ReactNode;
}

export const CampaignLogsTable = ({
  data,
  fetchNextPage,
  hasNextPage,
  currentPageIndex,
  setCurrentPageIndex,
  rowCount,
  isLoading,
  isFetchingNextPage,
  lastSeen,
  onReplay,
  campaign_senders,
  onRowClick,
  getRowClickable,
  getRowHref,
  onStopClick,
  onResumeClick,
  campaignStatus,
  isAgentic,
  hideReplay,
  columnVisibility,
  onColumnVisibilityChange,
  sorting,
  onSortingChange,
  paginationCenterContent,
}: CampaignLogsTableProps) => {
  const { t } = useTranslation();

  const tableColumns = useMemo(
    () =>
      columns({
        lastSeen,
        onReplay,
        campaign_senders,
        onStopClick,
        onResumeClick,
        campaignStatus,
        isAgentic,
        hideReplay,
      }),
    [
      lastSeen,
      onReplay,
      campaign_senders,
      onStopClick,
      onResumeClick,
      campaignStatus,
      isAgentic,
      hideReplay,
    ]
  );

  return (
    <DataTable.Root<CampaignSubscriber, unknown>
      fill
      className="min-h-0 flex-1"
      columns={tableColumns}
      data={data}
      pagination
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      currentPageIndex={currentPageIndex}
      setCurrentPageIndex={setCurrentPageIndex}
      rowCount={rowCount}
      resourceName="subscriber"
      enableRowHover
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={onColumnVisibilityChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
      manualSorting
      onRowClick={onRowClick}
      getRowClickable={getRowClickable}
      getRowHref={getRowHref}
      emptyStateMessage={t('common.no_results')}
    >
      <DataTable.Content />
      <DataTable.Pagination centerContent={paginationCenterContent} />
    </DataTable.Root>
  );
};
